import {
  mapProductsFromVariantsAcrossMultipleProducts,
  getVariantStockStatus,
  getTotalInStock,
  getTotalValueFromAddedVariants,
  getTotalValueFromAdddedVariantsInGB,
  mapItemsFromVariants,
} from "./marketingTrackingUtils";
import { itemsDataTemplate } from "./itemsDataTemplate";
import { getTotalUniqueSizes } from "../getTotalUniqueSizes";
import getRuntimeConstants from "../../runtimeConstants";

const HERO_COMPONENT = "pdp_hero";

const getBrowseCurrency = () =>
  getRuntimeConstants().webContext?.browseCurrency;

function getNumericCid(cid) {
  if (cid === null || cid === undefined) {
    return null;
  }

  if (typeof cid === "string" && cid.trim() === "") {
    return null;
  }

  const numericCid = Number(cid);
  return Number.isFinite(numericCid) ? numericCid : null;
}

function getProductId(product) {
  if (!product) {
    return null;
  }

  const productId = product.id ?? product.productCode ?? null;
  return productId === null || productId === undefined
    ? null
    : String(productId);
}

export function addToWishlistTemplate({
  product,
  variantsAdded,
  component_name = HERO_COMPONENT,
  recommendationsRequestId,
}) {
  const productAdded =
    variantsAdded && variantsAdded.length > 0
      ? variantsAdded.map((variant) =>
          itemsDataTemplate({
            products: product,
            variant,
            getQuantity: true,
          }),
        )
      : [{ ...buildProductLevelItem(product), quantity: 1 }];

  return {
    add_to_wishlist: {
      component_name,
      ...(recommendationsRequestId && {
        source_recommendations_request_id: recommendationsRequestId,
      }),
      ecommerce: {
        currency: "GBP",
        localCurrencyCode:
          variantsAdded?.[0]?.price?.currency ?? getBrowseCurrency() ?? "GBP",
        value:
          product?.price?.xrp?.value ??
          product?.priceData?.xrp?.value ??
          product?.priceData?.current?.value ??
          0,
        valueLocalCurrency:
          product?.price?.current?.value ??
          product?.priceData?.current?.value ??
          0,
        items: productAdded,
      },
    },
  };
}

export function pageLoadMixAndMatchTemplate(
  parentProduct,
  { hostAndPath, urlParams, requestId, recommendationsRequestId },
) {
  const mixAndMatchProducts = parentProduct.products;
  const category = mixAndMatchProducts[0].productType?.name;

  const result = {
    productId: mixAndMatchProducts[0].id,
    productCode: mixAndMatchProducts[0].productCode,
    productUrl: hostAndPath,
    cid: getNumericCid(urlParams?.cid),
    ...(requestId && { request_id: requestId }),
    ...(recommendationsRequestId && {
      source_recommendations_request_id: recommendationsRequestId,
    }),
    ...(category && { category }),
    ecommerce: {
      currency: "GBP",
      localCurrencyCode: mixAndMatchProducts[0].price.currency,
      value: mixAndMatchProducts[0].price.xrp.value,
      valueLocalCurrency: mixAndMatchProducts[0].price.current.value,
      items: mapProductsFromVariantsAcrossMultipleProducts({
        mixAndMatchProducts,
        getQuantity: true,
      }),
    },
  };

  return result;
}

export function pageLoadSingleProductTemplate(
  products,
  { hostAndPath, urlParams, requestId, recommendationsRequestId },
) {
  const category = products.productType?.name;
  const result = {
    productId: products.id,
    productCode: products.productCode,
    availableSku: getTotalInStock(products.variants),
    availableSizes: getTotalUniqueSizes({ variants: products.variants }),
    productUrl: hostAndPath,
    cid: getNumericCid(urlParams?.cid),
    ...(requestId && { request_id: requestId }),
    ...(recommendationsRequestId && {
      source_recommendations_request_id: recommendationsRequestId,
    }),
    variantStockStatus: getVariantStockStatus(products.variants),
    ...(category && { category }),
    ecommerce: {
      currency: "GBP",
      localCurrencyCode: products.variants[0].price.currency,
      value: products.price.xrp.value,
      valueLocalCurrency: products.price.current.value,
      items: mapItemsFromVariants({
        products,
        variants: products.variants,
        getQuantity: true,
      }),
    },
  };
  return result;
}

export function addToCartSingleProductTemplate({
  products,
  variantAdded,
  context = HERO_COMPONENT,
  requestId,
  recommendationsRequestId,
}) {
  const productAdded = itemsDataTemplate({
    products,
    variant: variantAdded,
    getQuantity: true,
  });

  return {
    add_to_cart: {
      component_name: context,
      ...(requestId && context === HERO_COMPONENT && { request_id: requestId }),
      ...(recommendationsRequestId &&
        context === HERO_COMPONENT && {
          source_recommendations_request_id: recommendationsRequestId,
        }),
      ecommerce: {
        currency: "GBP",
        localCurrencyCode: variantAdded.price.currency,
        value: productAdded.price,
        valueLocalCurrency: productAdded.priceLocalCurrency,
        items: [productAdded],
      },
    },
  };
}

export function addToCartMultipleProductTemplate({
  products,
  variantsAddedList,
  context = HERO_COMPONENT,
  requestId,
  recommendationsRequestId,
}) {
  const productsAdded = mapItemsFromVariants({
    products,
    variants: variantsAddedList,
    getQuantity: true,
  });

  return {
    add_to_cart: {
      component_name: context,
      ...(requestId && context === HERO_COMPONENT && { request_id: requestId }),
      ...(recommendationsRequestId &&
        context === HERO_COMPONENT && {
          source_recommendations_request_id: recommendationsRequestId,
        }),
      ecommerce: {
        currency: "GBP",
        localCurrencyCode: variantsAddedList[0].priceObj.currency,
        value: getTotalValueFromAdddedVariantsInGB(productsAdded),
        valueLocalCurrency: getTotalValueFromAddedVariants(productsAdded),
        items: productsAdded,
      },
    },
  };
}

export function notifyMeButtonClickTemplate({
  product,
  variant,
  component_name = "pdp_hero",
  isAppDownloadNudgeShown = false,
}) {
  const item = itemsDataTemplate({ products: product, variant });
  return {
    notify_me_button_click: {
      component_name,
      ...(isAppDownloadNudgeShown && {
        content_response: "download_the_app_nudge",
      }),
      ecommerce: {
        items: [item],
      },
    },
  };
}

export function mspOnboardingCompletedTemplate({ product }) {
  const productId = getProductId(product);

  if (!productId) {
    return null;
  }

  return {
    msp_onboarding_completed: {
      productId,
    },
  };
}

export function mspRecommendationDisplayedTemplate({
  product,
  recommendedSizeCount,
}) {
  const productId = getProductId(product);

  if (!productId) {
    return null;
  }

  return {
    msp_recommendation_displayed: {
      productId,
      msp_recommended_size_count: recommendedSizeCount,
    },
  };
}

export function mspSizeSelectedTemplate({ product, isRecommended }) {
  const productId = getProductId(product);

  if (!productId) {
    return null;
  }

  return {
    msp_size_selected: {
      productId,
      msp_selected_size_recommended: Boolean(isRecommended),
    },
  };
}

export function reviewTranslationClickedTemplate({
  product,
  reviewId,
  sourceLanguage,
  targetLanguage,
}) {
  const productId = getProductId(product);

  if (!productId) {
    return null;
  }

  return {
    review_translation_clicked: {
      productId,
      review_id: reviewId,
      source_language: sourceLanguage,
      target_language: targetLanguage,
    },
  };
}

export function reviewCoverageDisplayedTemplate({
  product,
  totalReviewCount,
  hasMinimumCoverage,
  locale,
}) {
  const productId = getProductId(product);

  if (!productId) {
    return null;
  }

  return {
    review_coverage_displayed: {
      productId,
      // totalReviewCount can arrive as a string from the ratings API, but schema requires integer
      total_review_count: parseInt(totalReviewCount, 10),
      has_minimum_coverage: Boolean(hasMinimumCoverage),
      locale,
    },
  };
}

// The recs API can return a null price, which the datalayer rejects; these fields are optional, so omit instead.
function toNumericPrice(value) {
  return Number.isFinite(value) ? value : undefined;
}

function buildProductLevelItem(product) {
  const productId = product.productId ?? product.id;
  const priceLocalCurrency =
    toNumericPrice(product.price?.current?.value) ??
    toNumericPrice(product.priceData?.current?.value);
  const price =
    toNumericPrice(product.price?.xrp?.value) ??
    toNumericPrice(product.priceData?.xrp?.value) ??
    priceLocalCurrency;

  const item = {
    productId: productId?.toString(),
    item_name: product.name ?? product.productTitle,
    itemType: "Product",
    ...(price !== undefined && { price }),
    ...(priceLocalCurrency !== undefined && { priceLocalCurrency }),
    google_business_vertical: "retail",
  };

  if (product.brandName) {
    item.item_brand = product.brandName;
  }

  if (product.productCode) {
    item.productCode = product.productCode.toString();
  }

  return item;
}

export function viewItemListTemplate({
  products,
  listName,
  includeSponsoredMetadata = false,
  recsRequestId,
}) {
  const items = (products || []).map((product, index) => {
    const isAd = Boolean(product.advertisement);

    if (product.variants && product.variants.length > 0) {
      return itemsDataTemplate({
        products: product,
        variant: product.variants[0],
        isAd,
        ...(includeSponsoredMetadata && {
          index: index + 1,
          isAd,
        }),
      });
    }

    return {
      ...buildProductLevelItem(product),
      is_ad: isAd,
      ...(includeSponsoredMetadata && {
        index: index + 1,
        is_ad: isAd,
      }),
    };
  });

  const browseCurrency = getBrowseCurrency();
  const adCount = items.filter((item) => item?.is_ad === true).length;

  return {
    view_item_list: {
      item_list_name: listName,
      ...(recsRequestId && { recommendations_request_id: recsRequestId }),
      has_sponsored_ads: adCount > 0,
      ad_count: adCount,
      ecommerce: {
        currency: "GBP",
        ...(browseCurrency && { localCurrencyCode: browseCurrency }),
        items: items,
      },
    },
  };
}

export function productComponentImpressionTemplate({
  products,
  itemListName,
  row,
}) {
  const items = (products || []).map((product) =>
    product.variants && product.variants.length > 0
      ? itemsDataTemplate({
          products: product,
          variant: product.variants[0],
        })
      : buildProductLevelItem(product),
  );

  const browseCurrency = getBrowseCurrency();

  return {
    product_component_impression: {
      item_list_name: itemListName,
      ...(typeof row === "number" ? { row } : {}),
      ecommerce: {
        currency: "GBP",
        ...(browseCurrency && { localCurrencyCode: browseCurrency }),
        items: items,
      },
    },
  };
}
